import { useCallback, useEffect, useRef, useState } from 'react';

import { applyDragReorder, getAreaDropGaps, getEffectiveDropGap, isSameOrderByName } from './utils';

import type { DragEvent, RefObject } from 'react';
import type { FavoriteService } from '@kc-console/context';
import type { DropSide, FavoriteDragState } from './constants';

interface UseFavoriteDndReorderParams {
  /** 서버 상태 즐겨찾기 목록 — 변경 시 localOrder를 동기화한다 */
  favoriteServices: FavoriteService[];
  /** Safari :hover 잔류 우회용 pointer-events 차단 대상(노출 목록 ul) */
  listRef: RefObject<HTMLUListElement | null>;
  /** 드래그 종료 시 이동 결과가 메뉴에 표시되지 않으면(탑바 표시/취소) 더보기 메뉴를 닫는다 */
  onCloseMoreMenu: () => void;
  /** drop 커밋 시 변경된 순서를 저장한다 (useEditFavoriteService.mutate 주입) */
  onCommitOrder: (next: FavoriteService[]) => void;
  /** 노출 목록 길이 = 노출/숨김 경계 인덱스 — 영역별 gap 게이팅 기준 */
  visibleCount: number;
  /** 커밋될 순서로 reflow 시 이동 아이템이 실제로 메뉴(숨김 영역)에 표시될지 예측 (메뉴 유지/닫기 판정용) */
  willDisplayInMenu: (next: FavoriteService[], movedName: string) => boolean;
}

/**
 * 즐겨찾기 DnD 순서 변경 오케스트레이션 훅
 * @description
 * - localOrder: 드래그 중 임시 순서를 담는 로컬 상태. 서버 상태(favoriteServices) 변경 시 동기화 —
 *   drop 후 optimistic update가 favoriteServices를 localOrder와 동일하게 갱신하므로 순서가 리셋되지 않는다.
 * - dragState(from/to/side): 현재 삽입 위치. HTML5 DnD의 end 이벤트가 리렌더 전에 발생하므로
 *   handleDragHover에서 ref를 동기로 갱신해 최신 위치를 보장한다.
 * - 드롭 커밋: handleDragEnd에서 applyDragReorder로 재정렬 후 onCommitOrder 호출.
 * - 인디케이터: effectiveDropGap을 영역(노출 목록/더보기 메뉴)별로 게이팅해 반환한다.
 */
export const useFavoriteDndReorder = ({
  favoriteServices,
  listRef,
  onCommitOrder,
  onCloseMoreMenu,
  visibleCount,
  willDisplayInMenu,
}: UseFavoriteDndReorderParams) => {
  // drag 중 순서 임시 보관용 로컬 상태
  const [localOrder, setLocalOrder] = useState<FavoriteService[]>(favoriteServices);
  // stale closure 없이 최신 값을 읽기 위한 ref
  const localOrderRef = useRef(localOrder);
  localOrderRef.current = localOrder;

  // 커서가 유효 드롭 영역(바/메뉴) 밖에 있는지. dragState는 유지한 채 Divider 표시/커밋 가부만 제어
  const [isOutsideDropZone, setIsOutsideDropZone] = useState(false);

  // favoriteServices(서버 상태)가 변경될 때 localOrder 동기화
  // drop 후 useEditFavoriteService의 optimistic update가 favoriteServices를 localOrder와
  // 동일하게 갱신하므로 동기화 시 순서가 리셋되지 않음
  useEffect(() => {
    setLocalOrder(favoriteServices);
  }, [favoriteServices]);

  const [dragState, setDragState] = useState<FavoriteDragState | null>(null);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  // 메뉴(Popper) 위 드롭에서 monitor.didDrop()이 false로 보고되는 경우를 우회
  const droppedOnMoreListRef = useRef(false);

  const handleDragHover = useCallback((from: number, to: number, side: DropSide) => {
    // 유효 아이템 위로 진입했으므로 바깥 상태를 해제한다
    setIsOutsideDropZone(false);
    // HTML5 DnD의 end 이벤트가 리렌더 전에 발생하므로 ref를 동기로 갱신해 최신 위치를 보장한다
    dragStateRef.current = { from, to, side };
    setDragState(prev => {
      if (prev?.from === from && prev?.to === to && prev?.side === side) return prev;
      return { from, to, side };
    });
  }, []);

  const handleMoreListDrop = useCallback(() => {
    droppedOnMoreListRef.current = true;
  }, []);

  const handleDragEnd = useCallback(
    (didDrop: boolean) => {
      // Safari: 드래그 종료 후 다음 포인터 이동 전까지 :hover가 갱신되지 않아 항목 hover 스타일이
      // 남는 WebKit 버그를 우회한다. 한 프레임 동안 목록의 pointer-events를 끊어 hover 재평가를 유도.
      const listEl = listRef.current;
      if (listEl) {
        listEl.style.pointerEvents = 'none';
        requestAnimationFrame(() => {
          listEl.style.pointerEvents = '';
        });
      }

      const dropped = didDrop || droppedOnMoreListRef.current;
      droppedOnMoreListRef.current = false;
      const current = dragStateRef.current;
      setDragState(null);
      setIsOutsideDropZone(false);

      if (!dropped || !current || current.from === current.to) {
        // 커밋 없음(취소/제자리 드롭): 메뉴 항목 위 제자리 드롭이면 표시가 변하지 않으므로 유지, 그 외 닫기
        const keepMenuOpen = dropped && current !== null && current.from >= visibleCount;
        if (!keepMenuOpen) {
          onCloseMoreMenu();
        }
        return;
      }

      // 드래그 중 서버 상태 갱신으로 localOrder가 변경되면 from이 범위를 벗어날 수 있다 — 커밋하지 않고 종료
      const moved = localOrderRef.current[current.from];
      if (!moved) {
        onCloseMoreMenu();
        return;
      }

      const next = applyDragReorder(localOrderRef.current, current);

      // 인접 no-op 이동(예: 탑바 끝 아이템 ↔ 메뉴 첫 위치 경계 드롭)은 순서가 그대로다 —
      // 제자리 드롭과 동일하게 처리하고 서버 요청을 생략한다 (메뉴 항목이면 메뉴 유지)
      if (isSameOrderByName(localOrderRef.current, next)) {
        if (current.from < visibleCount) {
          onCloseMoreMenu();
        }
        return;
      }

      // 더보기 메뉴 유지/닫기: 이동한 아이템이 reflow 후 "실제로 메뉴에 표시될 때"만 유지한다(결과 확인 목적).
      // 인디케이터 위치(to >= visibleCount) 기준을 쓰면 경계 드롭(메뉴 첫 위치)에서 어긋난다 —
      // 인디케이터는 메뉴 쪽에 보여도 reflow 후 아이템이 탑바 끝에 남는 케이스가 있어 표시 위치 예측으로 판정.
      if (!willDisplayInMenu(next, moved.name)) {
        onCloseMoreMenu();
      }

      setLocalOrder(next);
      onCommitOrder(next);
    },
    [listRef, onCommitOrder, onCloseMoreMenu, visibleCount, willDisplayInMenu],
  );

  // react-dnd(HTML5Backend)는 effectAllowed를 설정하지 않아 'uninitialized'(=모든 효과 허용) 상태가 된다.
  // 이 경우 항목 경계 등 dropEffect가 명시되지 않는 순간 브라우저가 기본값으로 copy('+') 커서를 띄운다.
  // dragstart에서 'move'로 고정해 copy를 원천 차단한다.
  // LiWrapper의 capture에 연결되므로 body로 portal되는 더보기 메뉴 출처 드래그에는 적용되지 않는다 —
  // 메뉴 출처에서는 copy 커서가 관측되지 않아 그대로 둔다.
  const handleDragStart = useCallback((e: DragEvent<HTMLElement>) => {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  }, []);

  // 드롭 indicator gap 계산 + 영역(노출/숨김)별 분기 — 규칙 설명은 각 함수 TSDoc 참고
  const effectiveDropGap = getEffectiveDropGap(dragState);
  const { favoriteListDropGap, moreListDropGap } = getAreaDropGaps({
    dragState,
    effectiveDropGap,
    isOutsideDropZone,
    visibleCount,
  });

  return {
    favoriteListDropGap,
    handleDragEnd,
    handleDragStart,
    handleDragHover,
    handleMoreListDrop,
    localOrder,
    moreListDropGap,
    /** FavoriteBodyDropTarget.onOutsideChange에 연결 */
    setIsOutsideDropZone,
  };
};
